/**
 * Country-aware monthly ad-spend floors for Lead Generation.
 * Add a market by setting `minCentsByCountry` on
 * `config.leadGenerationPlans.budgetRange`. Countries omitted from that
 * map use `minCents`.
 */

export type LeadGenAdSpendRange = {
  /** Default monthly minimum in USD cents. */
  minCents: number;
  maxCents: number;
  /**
   * ISO 3166-1 alpha-2 codes to a monthly minimum in USD cents.
   * Keys are matched case-insensitively.
   */
  minCentsByCountry?: Readonly<Record<string, number>>;
};

function countryMinimum(range: LeadGenAdSpendRange, country: string): number {
  const code = country.trim().toLowerCase();
  const overrides = range.minCentsByCountry ?? {};
  for (const [key, cents] of Object.entries(overrides)) {
    if (key.toLowerCase() === code) return cents;
  }
  return range.minCents;
}

function uniqueCountryCodes(countries: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const country of countries) {
    const code = country.trim().toLowerCase();
    if (code) seen.add(code);
  }
  return [...seen];
}

/**
 * Monthly minimum in USD cents for the selected markets.
 * Several countries use the highest minimum, so a lower-floor market
 * cannot pull a mixed campaign below a stricter one.
 * No countries yet uses the default minimum.
 */
export function getLeadGenMinAdSpendCents(
  range: LeadGenAdSpendRange,
  countries: readonly string[] = [],
): number {
  const codes = uniqueCountryCodes(countries);
  if (codes.length === 0) return range.minCents;
  return Math.max(...codes.map((code) => countryMinimum(range, code)));
}

/** Lowest floor any configured market allows. Use this as the schema bound. */
export function getLeadGenAbsoluteMinAdSpendCents(
  range: LeadGenAdSpendRange,
): number {
  return Math.min(
    range.minCents,
    ...Object.values(range.minCentsByCountry ?? {}),
  );
}

/** Slider step in cents that stays on the minimum's grid. */
export function getLeadGenAdSpendStepCents(minCents: number): number {
  const hundredDollars = 10_000;
  if (minCents % hundredDollars === 0) return hundredDollars;
  const fiftyDollars = 5_000;
  if (minCents % fiftyDollars === 0) return fiftyDollars;
  return 100;
}

export function formatLeadGenMinAdSpendMessage(minCents: number): string {
  const dollars = Math.round(minCents / 100);
  return `Minimum monthly ad spend is $${dollars.toLocaleString("en-US")} USD`;
}
